import { useId, useState } from 'react';

import { Button } from '../Button/Button.tsx';
import { Cell, Column, Row, Table, TableBody, TableHeader } from '../Table/Table.tsx';
import styles from './TrendChart.module.css';

export interface TrendPoint {
  /** The period, in words: `Sep 2026`. Also the axis label. */
  label: string;
  /** Null when there was nothing to measure; no bar is drawn. */
  value: number | null;
  /** The value in words for the readout and the table: `30 min`, `No data`. */
  valueText: string;
}

export interface TrendChartProps {
  /** What is plotted, e.g. "Waiting time, last 6 months". Names the chart and its table. */
  title: string;
  /** Oldest first. A short series: one bar per period. */
  points: readonly TrendPoint[];
  /** The table's value column heading, e.g. "Median wait". */
  valueLabel: string;
  /** The table's period column heading: "Month" unless the points are days or weeks. */
  periodLabel?: string;
}

/**
 * A single-series column chart for a few periods (the dataviz conventions: one hue from the
 * tokens, thin bars with 4px rounded ends from one baseline, a recessive axis, a label on the
 * latest bar only). Each bar is focusable and reads its period and value; hovering or
 * focusing a bar shows it in the readout line. "Show as table" gives the same numbers as a
 * table, for screen readers, print and anyone who prefers it. Colour comes from tokens, so
 * dark mode and every theme follow.
 */
export function TrendChart({ title, points, valueLabel, periodLabel = 'Month' }: TrendChartProps) {
  const [asTable, setAsTable] = useState(false);
  const [active, setActive] = useState<number | null>(null);
  const titleId = useId();
  const max = Math.max(0, ...points.map((p) => p.value ?? 0));
  const shown = active ?? points.length - 1;
  const readout = points[shown];
  return (
    <figure className={styles.figure} aria-labelledby={titleId}>
      <figcaption className={styles.caption}>
        <span id={titleId}>{title}</span>
        <Button
          dense
          variant="quiet"
          onPress={() => {
            setAsTable((v) => !v);
          }}
        >
          {asTable ? 'Show as chart' : 'Show as table'}
        </Button>
      </figcaption>
      {asTable ? (
        <Table flat aria-label={title}>
          <TableHeader>
            <Column isRowHeader>{periodLabel}</Column>
            <Column>{valueLabel}</Column>
          </TableHeader>
          <TableBody items={points.map((p, i) => ({ ...p, id: String(i) }))}>
            {(p) => (
              <Row id={p.id}>
                <Cell>{p.label}</Cell>
                <Cell>{p.valueText}</Cell>
              </Row>
            )}
          </TableBody>
        </Table>
      ) : (
        <>
          <p className={styles.readout} aria-live="polite">
            {readout === undefined ? '' : `${readout.label}: ${readout.valueText}`}
          </p>
          <ul className={styles.plot} aria-label={title}>
            {points.map((p, i) => {
              const height = p.value === null || max === 0 ? 0 : (p.value / max) * 100;
              const last = i === points.length - 1;
              return (
                <li
                  key={p.label}
                  className={styles.slot}
                  tabIndex={0}
                  aria-label={`${p.label}: ${p.valueText}`}
                  data-active={i === shown}
                  onPointerEnter={() => {
                    setActive(i);
                  }}
                  onPointerLeave={() => {
                    setActive(null);
                  }}
                  onFocus={() => {
                    setActive(i);
                  }}
                  onBlur={() => {
                    setActive(null);
                  }}
                >
                  <span className={styles.track} aria-hidden="true">
                    {last && p.value !== null ? (
                      <span className={styles.tip}>{p.valueText}</span>
                    ) : null}
                    {p.value === null ? null : (
                      <span
                        className={styles.bar}
                        data-zero={p.value === 0}
                        style={{ height: `${String(height)}%` }}
                      />
                    )}
                  </span>
                  <span className={styles.axis} aria-hidden="true">
                    {p.label}
                  </span>
                </li>
              );
            })}
          </ul>
        </>
      )}
    </figure>
  );
}
